import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton
} from '@invent/wl-ui-kit-next/ui';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/design';
import { ApiError, ProblemType, errorMessage } from '@/domains/api';
import {
  GivenAnswers,
  type IRouteAnswer,
  TeamRoutingQuestion,
  answerRefusal,
  routingAnswers
} from '@/domains/auth';
import { surveyApi } from '../api';
import type { IMyCycle, IMyTeamRouting, IMyTeamRoutingRead } from '../api/contracts';
import { PORTAL_PATHS, responseFormPath } from '../paths';
import { surveyTitle } from '../survey-labels';
import { useCanAdd } from './catalogue';
import {
  RiaNotice,
  openResponse,
  routedPath,
  startFailureMessage,
  startLabel,
  teamsCycleOf
} from './start-survey-card';

/**
 * `teamRoutingPath` — `/portal/surveys/teams/:participantId/routing`: a team already on file
 * answering its own routing questions (the teams onboarding plan's R9, over survey-api's
 * `/my/participants/{id}/team-routing`). The catalogue sends a team here while survey-api has
 * routed it nowhere, since a start would be refused `path_required`, and while its firm alone
 * routes it (`source: 'firm'`, block 1's Q4), since a start would open with the private-wealth
 * answer blank and locked.
 *
 * **It asks what survey-api serves, and nothing else decides whether it asks** (block 1's B1-5):
 * the read carries a question for a team routed nowhere and for one its firm routes — a wirehouse
 * team is still asked whether it is part of a private wealth channel (SR-416's `TeamGating`) — and
 * none once the team's answers or Veritant decided. The page asks the questions one at a time
 * through `TeamRoutingQuestion` —
 * registration's route step's own rendering — under the same rule: survey-api keeps no answer
 * between calls, so the page holds the answers given so far, posts all of them each time, and
 * a reload starts from the first question, which the page says. When an outcome lands, it says
 * which survey the team takes and offers to start it — naming no path, which survey-api fills
 * from the routing.
 *
 * **A decided outcome is shown, never re-asked** (the owner's Q5 (a)). survey-api takes a new
 * post over the team's own answers, and a new path moves the team's live responses — a submitted
 * one reopened — so the routed card says what decided it and offers nothing. Only Veritant's
 * decision closes the routing to the team: an answer posted under one is `409
 * team-routing-closed`, said as Veritant's, with nothing offered. 404 is "not one of your teams",
 * the route's answer for a participant the caller does not act for and for one that is no team
 * alike.
 *
 * **The start is made in the Teams cycle, and a catalogue that cannot be read is said.** The
 * account's surveys (`/my/cycles`) are read beside the routing for the cycle a start is made in —
 * the one that lists the team with its routing (`teamsCycleOf`), not the first that lists it at
 * all. That read is loading, failed or loaded, and each is drawn as itself: a failed read is
 * said in words, never as "no Teams cycle is open", which would be a claim about survey-api
 * that the page cannot make. It is read again when an outcome lands, since the routing the list
 * carries has just changed.
 *
 * Nothing here derives the routing or the next question, and nothing renders who at Veritant
 * decided or their note: the team's read carries neither.
 */
export function TeamRoutingPage() {
  const { participantId = '' } = useParams();
  // Keyed by the team, so a link from one team's page to another's starts over rather than
  // carrying the first team's answers across.
  return <TeamRouting key={participantId} participantId={participantId} />;
}

type RoutingRead =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed'; message: string }
  | { kind: 'loaded'; read: IMyTeamRoutingRead };

/** The account's surveys, read for the cycle a start is made in — see the module's JSDoc. */
type CyclesRead =
  | { kind: 'loading' }
  | { kind: 'failed'; message: string }
  | { kind: 'loaded'; cycles: IMyCycle[] };

function TeamRouting({ participantId }: { participantId: string }) {
  const [read, setRead] = React.useState<RoutingRead>({ kind: 'loading' });
  const [cycles, setCycles] = React.useState<CyclesRead>({ kind: 'loading' });
  const [given, setGiven] = React.useState<readonly IRouteAnswer[]>([]);
  const [closed, setClosed] = React.useState(false);
  const cyclesRead = React.useRef<Nullable<AbortController>>(null);

  /** One read of the account's surveys; a later one supersedes an earlier still out. Not a
   *  failure of the page when it fails: the routing still says which survey the team takes. */
  const readCycles = React.useCallback(() => {
    cyclesRead.current?.abort();
    const controller = new AbortController();
    cyclesRead.current = controller;
    setCycles({ kind: 'loading' });
    surveyApi.myCycles(controller.signal).then(
      (loaded) => {
        if (!controller.signal.aborted) setCycles({ kind: 'loaded', cycles: loaded });
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        setCycles({ kind: 'failed', message: errorMessage(error, 'The request failed') });
      }
    );
  }, []);

  React.useEffect(() => {
    const controller = new AbortController();
    surveyApi.teamRouting(participantId, controller.signal).then(
      (loaded) => setRead(loaded ? { kind: 'loaded', read: loaded } : { kind: 'missing' }),
      (error: unknown) => {
        if (controller.signal.aborted) return;
        setRead({ kind: 'failed', message: errorMessage(error, 'Could not load your team') });
      }
    );
    readCycles();
    return () => {
      controller.abort();
      cyclesRead.current?.abort();
    };
  }, [participantId, readCycles]);

  return (
    <>
      <PageHeader
        title="Which survey your team takes"
        lede="Your answers decide it."
      />
      <div className="mx-auto grid w-full max-w-[42rem] gap-6 px-6 pt-6 pb-16">
        {read.kind === 'loading' && <Skeleton className="h-64 w-full" />}
        {read.kind === 'missing' && <NotYourTeam />}
        {read.kind === 'failed' && (
          <Alert variant="destructive">
            <AlertTitle>Something went wrong</AlertTitle>
            <AlertDescription>{read.message}</AlertDescription>
          </Alert>
        )}
        {read.kind === 'loaded' &&
          (closed ? (
            <RoutingClosed />
          ) : read.read.question ? (
            <Questions
              participantId={participantId}
              routing={read.read.routing}
              question={read.read.question}
              given={given}
              onAnswered={(next, answers) => {
                setRead({ kind: 'loaded', read: next });
                // A question keeps the answers that led to it; an outcome ends the asking, and
                // changes the routing the account's surveys carry, so they are read again.
                setGiven(next.question ? answers : []);
                if (!next.question) readCycles();
              }}
              onClosed={() => setClosed(true)}
              onMissing={() => setRead({ kind: 'missing' })}
            />
          ) : (
            <Routed participantId={participantId} routing={read.read.routing} cycles={cycles} />
          ))}
      </div>
    </>
  );
}

/**
 * The questions, one at a time. `given` is every answer so far, and each request carries all of
 * them: the next question, or an outcome, is what survey-api makes of the whole set. "Change my
 * last answer" posts the set without its last answer, which survey-api answers with the question
 * that answer was to.
 *
 * A refusal of an answer comes back as `invalid-body` `problems[]` on `answers.<code>` — said on
 * the question it names, and nothing is re-read. One naming no problem at all is said whole, as
 * any other failure is: a refusal with nothing drawn for it would read as a press that did
 * nothing.
 */
function Questions({
  participantId,
  routing,
  question,
  given,
  onAnswered,
  onClosed,
  onMissing
}: {
  participantId: string;
  routing: IMyTeamRouting;
  question: NonNullable<IMyTeamRoutingRead['question']>;
  given: readonly IRouteAnswer[];
  onAnswered: (next: IMyTeamRoutingRead, given: readonly IRouteAnswer[]) => void;
  onClosed: () => void;
  onMissing: () => void;
}) {
  const [pending, setPending] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<ApiError>>(null);
  const [failure, setFailure] = React.useState<Nullable<string>>(null);
  const [retaken, setRetaken] = React.useState<Nullable<IRouteAnswer>>(null);

  const post = async (answers: readonly IRouteAnswer[]) => {
    setPending(true);
    setRefusal(null);
    setFailure(null);
    try {
      const next = await surveyApi.answerTeamRouting(participantId, routingAnswers(answers));
      if (next === null) onMissing();
      else onAnswered(next, answers);
    } catch (error) {
      if (error instanceof ApiError && error.isProblem(ProblemType.teamRoutingClosed)) {
        onClosed();
      } else if (error instanceof ApiError && error.status === 404) {
        onMissing();
      } else if (
        error instanceof ApiError &&
        error.isProblem(ProblemType.invalidBody) &&
        (error.problem?.problems?.length ?? 0) > 0
      ) {
        setRefusal(error);
      } else {
        setFailure(errorMessage(error, 'Could not record your answer'));
      }
    } finally {
      setPending(false);
    }
  };

  const key = `answers.${question.code}`;
  const problems = refusal?.problem?.problems ?? [];
  const messages = problems
    .filter((problem) => problem.target === key)
    .map((problem) => answerRefusal(problem.reason));
  // A refusal naming another question than the one on screen is said whole, never dropped.
  const elsewhere = problems
    .filter((problem) => problem.target !== key)
    .map((problem) => `${problem.target}: ${problem.reason}`);

  return (
    <Card>
      <CardHeader>
        <CardTitle>A few questions about your team</CardTitle>
        <CardDescription>{questionsLede(routing)}</CardDescription>
      </CardHeader>
      <CardContent>
        <GivenAnswers given={given} />

        <TeamRoutingQuestion
          // Keyed by the question and how far in it is, so a new question starts blank and a
          // taken-back one starts from the answer it had.
          key={`${question.code}|${given.length}`}
          question={question}
          initial={retaken?.question.code === question.code ? retaken.value : undefined}
          messages={messages}
          pending={pending}
          onAnswer={(value) => void post([...given, { question, value }])}
        />

        {(failure || elsewhere.length > 0) && (
          <p role="alert" className="mt-4 text-sm font-medium text-destructive">
            {failure ?? elsewhere.join(' ')}
          </p>
        )}

        <p className="mt-5 text-xs text-muted-foreground">
          Your answers are kept on this page only — if it is reloaded, the questions start again
          from the first.
        </p>

        {given.length > 0 && (
          <Button
            variant="link"
            className="mt-2 px-0"
            disabled={pending}
            onClick={() => {
              setRetaken(given[given.length - 1]);
              void post(given.slice(0, -1));
            }}
          >
            Change my last answer
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/** What the questions rest on: a team its firm routes is told the firm is on the wirehouse list,
 *  and that one question settles the survey; any other is told its answers decide it. */
function questionsLede({ source, firm }: IMyTeamRouting): string {
  if (source === 'firm') {
    const named = firm ? `, ${firm.label},` : '';
    return (
      `Your lead advisor’s firm${named} is on our wirehouse list. ` +
      'One question settles which survey your team takes.'
    );
  }
  const answers = 'Your answers decide which Teams survey your team takes.';
  return firm ? `${answers} Your team’s firm: ${firm.label}.` : answers;
}

/**
 * A team whose outcome is known: which survey it takes, what that rests on, and the way in —
 * a start in the open Teams cycle survey-api lists the team in (`teamsCycleOf`), naming no path,
 * or the team's response there if it has one. The RIA firm survey is said as the catalogue says
 * it. While the account's surveys cannot be read, that is said, and nothing about which cycles
 * are open.
 */
function Routed({
  participantId,
  routing,
  cycles
}: {
  participantId: string;
  routing: IMyTeamRouting;
  cycles: CyclesRead;
}) {
  const navigate = useNavigate();
  const canAdd = useCanAdd();
  const [starting, setStarting] = React.useState(false);
  const [error, setError] = React.useState<Nullable<string>>(null);

  if (routing.outcome === 'ria') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Your team’s survey</CardTitle>
          <CardDescription>{basis(routing)}</CardDescription>
        </CardHeader>
        <CardContent>
          <RiaNotice team={null} canAdd={canAdd} />
        </CardContent>
      </Card>
    );
  }

  const cycle =
    cycles.kind === 'loaded' ? teamsCycleOf(cycles.cycles, participantId)?.cycle : undefined;
  const path = cycle ? routedPath(cycle, routing) : undefined;
  const existing = cycle?.responses.find((response) => response.participantId === participantId);

  const start = async (open: IMyCycle) => {
    setStarting(true);
    setError(null);
    try {
      navigate(responseFormPath(await openResponse(open, participantId)));
    } catch (startError) {
      setError(startFailureMessage(startError, open));
      setStarting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Your team takes {path ? `the ${path.name}` : surveyTitle('TEAMS')}
        </CardTitle>
        <CardDescription>{basis(routing)}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Something went wrong</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {cycles.kind === 'loading' ? (
          <Skeleton className="h-10 w-64" />
        ) : cycles.kind === 'failed' ? (
          <p role="alert" className="text-sm text-destructive">
            Could not read your surveys — {cycles.message.replace(/\.$/, '')}. Reload the page to
            try again; the start is on{' '}
            <Link to={PORTAL_PATHS.surveys} className="font-medium underline">
              your surveys
            </Link>{' '}
            too.
          </p>
        ) : !cycle ? (
          <p className="text-sm text-muted-foreground">
            No Teams cycle is open for your team right now. Its survey appears on{' '}
            <Link to={PORTAL_PATHS.surveys} className="font-medium underline">
              your surveys
            </Link>{' '}
            when one opens.
          </p>
        ) : existing ? (
          <Button
            variant="outline"
            className="justify-self-start"
            onClick={() => navigate(responseFormPath(existing.responseId))}
          >
            Open your team’s response
          </Button>
        ) : (
          <Button
            variant="outline"
            className="justify-self-start"
            disabled={starting}
            onClick={() => void start(cycle)}
          >
            <Plus />
            {starting ? 'Opening…' : startLabel(cycle, path)}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/** What a known routing rests on, in the team's words — never who at Veritant decided it, which
 *  the team's read does not carry. */
function basis(routing: IMyTeamRouting): string {
  switch (routing.source) {
    case 'firm':
      return 'Your lead advisor’s firm is on our wirehouse list.';
    case 'answers':
      return 'Your team’s answers decided it.';
    case 'staff':
      return 'Veritant decided it.';
    default:
      return 'It is decided from what our records hold about your team.';
  }
}

/** `409 team-routing-closed`: Veritant has decided, the one decision survey-api closes the routing
 *  with since SR-416 (`source: 'staff'`), and the page offers nothing — no answer would be
 *  taken. */
function RoutingClosed() {
  return (
    <Alert>
      <AlertTitle>Your team’s routing is not yours to answer</AlertTitle>
      <AlertDescription>
        <p>
          Veritant has decided which survey your team takes, so there is nothing for the team to
          answer.
        </p>
        <Link
          to={PORTAL_PATHS.surveys}
          className="mt-2 inline-block font-medium text-[var(--color-royal-blue)] underline"
        >
          Go to your surveys
        </Link>
      </AlertDescription>
    </Alert>
  );
}

function NotYourTeam() {
  return (
    <Alert>
      <AlertTitle>This is not one of your teams</AlertTitle>
      <AlertDescription>
        <p>There is no team of yours at this address, so there is nothing to answer here.</p>
        <Link
          to={PORTAL_PATHS.surveys}
          className="mt-2 inline-block font-medium text-[var(--color-royal-blue)] underline"
        >
          Go to your surveys
        </Link>
      </AlertDescription>
    </Alert>
  );
}
